// ======================================================================================================
// Private routing
// - Routes/redirects accessible to authorised users (role dependent)
// ======================================================================================================

import useGetMe from '@/api/queries/me/useGetMe';
import { useGetMyRestProfile } from '@/api/rest/users/useGetMyRestProfile';
import { actions as usersActions } from '@/api/users';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlausibleProperties } from '@/hooks/usePlausibleProperties';
import { useShowParentAccountAdminLayout } from '@/hooks/useShowParentAccountAdminLayout';
import PageSkeleton from '@/ui/components/pageSkeleton/PageSkeleton';
import { getSkeletonContentForPath } from '@/ui/components/pageSkeleton/skeletonRegistry';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import WebUtil from '@payaca/shared-web/utilities/index';
import * as accountActions from '@payaca/store/account/accountActions';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { bindActionCreators } from 'redux';
import {
  getUsersPrivateRedirects,
  getUsersPrivateRoutes,
} from './privateRoutingConfig';
import RoutesAndRedirects from './RoutesAndRedirects';

const getAccountAccessProperties = (accountAccessInformation) => {
  return {
    trialPeriodIsExpired: accountAccessInformation
      ? !accountAccessInformation.hasActiveSubscription &&
        accountAccessInformation.isTrialPeriodExpired
      : false,
    hasExceededUserSeats: accountAccessInformation
      ? accountAccessInformation.hasActiveSubscription &&
        accountAccessInformation.hasExceededActiveSubscriptionUserSeats
      : false,
    hasBetaFeatures: accountAccessInformation?.features?.showBetaFeatures,
  };
};

const getProfileProperties = (profile) => {
  return {
    userHasContactNumber: !!profile?.contactNumber,
    accountIsSetup: profile?.accounts?.[0]?.legalBusinessName, // account exists with legal business name - will catch all users who have registered through web or mobile
  };
};

const PrivateRouting = ({
  match,
  accountAccessInformation,
  profile,
  users,
}) => {
  const location = useLocation();
  const { showParentAccountAdminLayout } = useShowParentAccountAdminLayout();
  const { userHasRequiredPermission } = usePermissions();
  usePlausibleProperties();

  useEffect(() => {
    accountActions.requestGetAccountAccessInformation();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has an unnecessary dependency: accountActions.
  }, [accountActions]);

  // Route components assume the profile and permissions are loaded before they
  // mount (e.g. useAccount() returns non-null). Hold the gate below until the
  // TanStack sources have settled too. On error the gate opens and helpers
  // return empty/undefined, matching the legacy behaviour on a failed fetch.
  const { data: restProfile, isLoading: isLoadingRestProfile } =
    useGetMyRestProfile();
  const { isLoading: isLoadingMe } = useGetMe();

  // Sync the legacy Redux profile copy (state.users.myProfile) from the query
  // result so legacy selectors keep working without their own fetch.
  useEffect(() => {
    if (restProfile) {
      users.storeMyProfile(restProfile);
    }
  }, [restProfile, users]);

  const isMobile = useMemo(() => WebUtil.isMobileScreen(), []);
  const isFormsRoute = useMemo(() => {
    return !location.pathname.includes('/forms');
  }, [location]); // forms is web-optimised

  const { trialPeriodIsExpired, hasExceededUserSeats, hasBetaFeatures } =
    useMemo(
      () => getAccountAccessProperties(accountAccessInformation),
      [accountAccessInformation]
    );
  const { userHasContactNumber, accountIsSetup } = useMemo(
    () => getProfileProperties(profile),
    [profile]
  );

  const canUpdateSubscription = userHasRequiredPermission([
    AccountsPermissions.UPDATE_SUBSCRIPTION,
  ]);

  const availableRoutes = useMemo(() => {
    return getUsersPrivateRoutes({
      canUpdateSubscription,
      isMobile,
      userHasRequiredPermission,
      userHasContactNumber,
      accountIsSetup,
      trialPeriodIsExpired,
      hasExceededUserSeats,
      hasBetaFeatures,
      isFormsRoute,
      showParentAccountAdminRoutes: showParentAccountAdminLayout,
    });
  }, [
    canUpdateSubscription,
    isMobile,
    hasExceededUserSeats,
    accountIsSetup,
    trialPeriodIsExpired,
    userHasContactNumber,
    userHasRequiredPermission,
    hasBetaFeatures,
    isFormsRoute,
    showParentAccountAdminLayout,
  ]);

  const availableRedirects = useMemo(() => {
    return getUsersPrivateRedirects({
      userHasRequiredPermission,
      canUpdateSubscription,
      isMobile,
      accountIsSetup,
      hasExceededUserSeats,
      trialPeriodIsExpired,
      userHasContactNumber,
      isFormsRoute,
      showParentAccountAdminRoutes: showParentAccountAdminLayout,
    });
  }, [
    userHasRequiredPermission,
    canUpdateSubscription,
    isMobile,
    accountIsSetup,
    hasExceededUserSeats,
    trialPeriodIsExpired,
    userHasContactNumber,
    isFormsRoute,
    showParentAccountAdminLayout,
  ]);

  if (!Object.keys(profile).length || isLoadingRestProfile || isLoadingMe) {
    const skeletonContent = getSkeletonContentForPath(location.pathname);
    return skeletonContent ? (
      <PageSkeleton content={skeletonContent} />
    ) : (
      <LoaderOverlay />
    );
  }

  return (
    <RoutesAndRedirects
      availableRoutes={availableRoutes}
      availableRedirects={availableRedirects}
      matchPath={match.path}
    />
  );
};

export default connect(
  (state) => ({
    dismissMobileMode: state.app.dismissMobileMode,
    profile: state.users.myProfile,
    accountAccessInformation: state.account.accountAccessInformation,
  }),
  (dispatch) => ({
    users: bindActionCreators(usersActions, dispatch),
    accountActions: bindActionCreators(accountActions, dispatch),
  })
)(PrivateRouting);
